import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Check, Send, Clock, ShieldCheck, LogIn, GraduationCap } from 'lucide-react';
import useSEO from '@/hooks/useSEO';

const BUILT_NOW = [
    { title: 'Safe for classrooms by design', body: 'No behavioural advertising anywhere on the platform. No user to user messaging. No public profiles. These are permanent commitments, built into every tier today.' },
    { title: 'Schools Data Processing Agreement', body: 'Private to schools — request a copy from feedback@linguisticquest.com or use the link below. Names Southern African Traders Ltd as processor and your school as controller. Ready for your counsel to review and sign.' },
    { title: 'Quote by email', body: 'Tell us roughly how many students and the languages you want and we respond within two working days.' },
];

const ROADMAP = [
    { title: 'Teacher dashboard', body: 'Assign themes and puzzle types to a class, review per student progress, add and remove students.' },
    { title: '30 seats per classroom licence', body: 'Pooled student seats with a join code so students can log in directly, no parent email required for under 13s under a school contract.' },
    { title: 'CSV progress export', body: 'Downloadable class gradebooks with scores, times, accuracy, and words learned per student.' },
    { title: 'Google Workspace and Microsoft 365 SSO', body: 'One click login for students and teachers tied to their school identity.' },
    { title: 'PayPal Invoicing for schools, bank transfer for districts', body: 'Billing without card entry, net 30 terms, PO accepted.' },
];

export default function Schools() {
    useSEO({
        title: 'Schools — Linguistic Quest classroom licences',
        description: 'Linguistic Quest for schools: pooled student seats, teacher dashboard, CSV gradebook export, ad-free, signed Data Processing Agreement. Request a quote for your school.',
        path: '/schools',
    });
    const [status, setStatus] = useState('idle');
    const [form, setForm] = useState({
        name: '', email: '', subject: 'Schools enquiry', message: '', source: 'schools_landing',
    });
    const submit = async (e) => {
        e.preventDefault();
        setStatus('sending');
        try { await api.post('/contact', form); setStatus('sent'); setForm({ ...form, message: '' }); }
        catch { setStatus('error'); }
    };

    return (
        <main data-testid="page-schools">
            {/* Hero */}
            <section className="bg-gradient-to-br from-lq-sky-100 via-lq-paper to-lq-purple-50 border-b border-lq-ink-100">
                <div className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-10 items-center">
                    <div>
                        <p className="text-sm font-semibold uppercase tracking-wider text-lq-sky-600">For schools</p>
                        <h1 className="mt-2 font-display text-4xl md:text-5xl font-semibold text-lq-ink-black">Game on, tongue on.</h1>
                        <p className="mt-5 text-lq-ink-700 text-lg leading-relaxed max-w-xl">
                            Linguistic Quest is built on values that matter to schools. Ad free. Privacy first. Parent led by default, teacher led on a schools contract. Currently in early access for schools while the classroom tooling is finished. Request a quote to join the first cohort.
                        </p>
                        <figure className="mt-6 rounded-2xl overflow-hidden border-2 border-white shadow-lg">
                            <img src="/brand/schools_family.jpg" alt="A family of two parents and three children solving puzzles together at a desk" className="w-full h-auto block" data-testid="schools-family-image" loading="lazy" decoding="async" />
                        </figure>
                    </div>
                    <div className="bg-white rounded-2xl border border-lq-ink-100 shadow-sm p-6 space-y-3">
                        <div className="flex items-center gap-2 text-lq-success font-semibold text-sm uppercase tracking-wider"><ShieldCheck size={16}/> Available today</div>
                        {BUILT_NOW.map((b) => (
                            <div key={b.title} className="flex gap-3 items-start">
                                <Check size={18} className="mt-0.5 text-lq-sky-600 flex-shrink-0" />
                                <div>
                                    <div className="font-semibold text-lq-ink-900 text-sm">{b.title}</div>
                                    <p className="text-xs text-lq-ink-500 leading-relaxed">{b.body}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Quick sign-in cards — discoverable entry points for teachers + students */}
            <section className="max-w-7xl mx-auto px-6 pt-12 pb-4" data-testid="schools-sign-in">
                <div className="grid md:grid-cols-2 gap-4">
                    <Link
                        to="/login?role=teacher"
                        data-testid="schools-teacher-sign-in"
                        className="group bg-white hover:bg-lq-purple-50/50 border-2 border-lq-purple-100 hover:border-lq-purple-500 rounded-2xl p-6 transition-colors block"
                    >
                        <div className="flex items-start gap-4">
                            <div className="h-12 w-12 rounded-xl bg-lq-purple-100 text-lq-purple-700 flex items-center justify-center flex-shrink-0">
                                <LogIn size={22}/>
                            </div>
                            <div className="min-w-0">
                                <h3 className="font-display text-xl font-semibold text-lq-ink-900">Teacher sign in</h3>
                                <p className="mt-1 text-sm text-lq-ink-700">Sign in with the email your school administrator used. First time? Use the one-time password we emailed you, then choose a new one.</p>
                                <span className="mt-3 inline-block text-sm font-semibold text-lq-purple-700 group-hover:underline">Open teacher login →</span>
                            </div>
                        </div>
                    </Link>

                    <Link
                        to="/schools/join"
                        data-testid="schools-student-sign-in"
                        className="group bg-white hover:bg-lq-sky-100/50 border-2 border-lq-sky-100 hover:border-lq-sky-600 rounded-2xl p-6 transition-colors block"
                    >
                        <div className="flex items-start gap-4">
                            <div className="h-12 w-12 rounded-xl bg-lq-sky-100 text-lq-sky-600 flex items-center justify-center flex-shrink-0">
                                <GraduationCap size={22}/>
                            </div>
                            <div className="min-w-0">
                                <h3 className="font-display text-xl font-semibold text-lq-ink-900">Student sign in</h3>
                                <p className="mt-1 text-sm text-lq-ink-700">Got a join code from your teacher? Enter your username (or first name) and PIN — no email needed.</p>
                                <span className="mt-3 inline-block text-sm font-semibold text-lq-sky-600 group-hover:underline">Open student sign in →</span>
                            </div>
                        </div>
                    </Link>
                </div>
            </section>

            {/* Honest roadmap */}
            <section className="max-w-7xl mx-auto px-6 py-16" data-testid="schools-roadmap">
                <div className="flex items-center gap-2 text-lq-warn font-semibold text-sm uppercase tracking-wider"><Clock size={16}/> On the schools roadmap (Phase 3)</div>
                <h2 className="mt-2 font-display text-3xl font-semibold text-lq-ink-900">Being built next, alongside our first school partners</h2>
                <p className="mt-3 text-lq-ink-700 max-w-2xl">
                    Transparency matters. The classroom features below are scheduled for Phase 3 of our build, not live yet. We are onboarding a small number of founding schools now to shape these with us. Early access schools receive a permanent discount and feature input.
                </p>
                <div className="mt-8 grid md:grid-cols-2 gap-4">
                    {ROADMAP.map((r) => (
                        <div key={r.title} className="bg-white border border-dashed border-lq-ink-300 rounded-xl p-5">
                            <div className="flex items-center gap-2">
                                <Clock size={16} className="text-lq-warn"/>
                                <h3 className="font-display text-lg font-semibold text-lq-ink-900">{r.title}</h3>
                            </div>
                            <p className="mt-1 text-sm text-lq-ink-500 leading-relaxed">{r.body}</p>
                        </div>
                    ))}
                </div>
                <div className="mt-6 text-sm text-lq-ink-700">
                    Schools may request the full <a href="/docs/legal/schools_dpa.md" target="_blank" rel="noopener" className="text-lq-purple-700 underline" data-testid="schools-dpa-link">Schools Data Processing Agreement (PDF / markdown)</a> for legal review before signing.
                </div>
            </section>

            {/* Quote form */}
            <section className="max-w-3xl mx-auto px-6 py-12">
                <h2 className="font-display text-3xl font-semibold text-lq-ink-900">Request a quote</h2>
                <p className="mt-2 text-lq-ink-500">Tell us your school name, approximate student count, and which languages you want to start with. We respond within two working days.</p>
                <form onSubmit={submit} className="mt-8 space-y-4" data-testid="schools-contact-form">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <label className="block">
                            <span className="text-sm font-medium text-lq-ink-900">Your name</span>
                            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2" data-testid="schools-form-name" />
                        </label>
                        <label className="block">
                            <span className="text-sm font-medium text-lq-ink-900">Email</span>
                            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2" data-testid="schools-form-email" />
                        </label>
                    </div>
                    <label className="block">
                        <span className="text-sm font-medium text-lq-ink-900">Tell us about your school or district</span>
                        <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2" data-testid="schools-form-message" />
                    </label>
                    <button disabled={status === 'sending'} className="inline-flex items-center gap-2 bg-lq-sky-600 text-white font-semibold px-6 py-3 rounded-full hover:bg-lq-sky-600/90 disabled:opacity-60" data-testid="schools-form-submit">
                        <Send size={16} /> {status === 'sending' ? 'Sending...' : status === 'sent' ? 'Sent, thank you' : 'Send enquiry'}
                    </button>
                    {status === 'error' && <p className="text-sm text-lq-error" role="alert">Something went wrong. Please email feedback@linguisticquest.com directly.</p>}
                </form>
            </section>
        </main>
    );
}
